import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BottomNav from "@/components/feature/BottomNav";
import { coachApi } from "@/api";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { locationStore } from "@/lib/location";
import type { CoachListItem } from "@/types/api";

const filterTabs = ["全部", "全身放松", "精油SPA", "中医推拿", "足部理疗"];

interface TherapistViewItem {
  id: number;
  name: string;
  title: string;
  badge: string;
  rating: string;
  experience: string;
  orders: number;
  tags: string[];
  specialty: string[];
  intro: string;
  avatar: string;
}

function mapCoachToView(item: CoachListItem): TherapistViewItem {
  const specialties = Array.isArray(item.specialties) && item.specialties.length
    ? item.specialties
    : ["平台认证", "上门服务"];
  const firstTag = Array.isArray(item.operationTags) && item.operationTags.length
    ? String(item.operationTags[0] || "").trim()
    : "";

  return {
    id: item.id,
    name: String(item.name || "技师"),
    title: String(item.level || "平台技师"),
    badge: firstTag,
    rating: Number(item.rating || 0).toFixed(1),
    experience: item.first_appoint ? `最早 ${item.first_appoint}` : "尽快上门",
    orders: Number(item.orderNum || 0),
    tags: specialties.slice(0, 4),
    specialty: specialties,
    intro: String(item.serviceSummary || "平台认证技师，可提供上门服务"),
    avatar:
      resolveCustomerAssetUrl(String(item.workPhoto || "")) ||
      "https://readdy.ai/api/search-image?query=professional%20massage%20therapist%20portrait&width=160&height=160&seq=therapists-page-avatar&orientation=squarish",
  };
}

export default function TherapistsPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState("全部");
  const [therapists, setTherapists] = useState<TherapistViewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadTherapists = async () => {
      setLoading(true);
      setError("");
      try {
        const currentLocation = locationStore.getFresh();
        const result = await coachApi.getCoachList({
          pageNum: 1,
          pageSize: 50,
          longitude: currentLocation?.longitude,
          latitude: currentLocation?.latitude,
          sortBy: "distance_asc",
        });
        if (!cancelled) {
          setTherapists((result.records || []).map(mapCoachToView));
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : "技师列表加载失败");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadTherapists();

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    return therapists.filter((item) =>
      activeFilter === "全部" ? true : item.specialty.some((value) => value.includes(activeFilter)),
    );
  }, [activeFilter, therapists]);

  return (
    <div
      className="flex flex-col min-h-screen pb-20"
      style={{ background: "#F7F7F7", maxWidth: 480, margin: "0 auto" }}
    >
      <div className="bg-white sticky top-0 z-40" style={{ borderBottom: "1px solid #F2F2F2" }}>
        <div className="px-4 pt-10 pb-3 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-xl" style={{ color: "#1a1a1a" }} />
          </button>
          <h1 className="text-base font-bold flex-1" style={{ color: "#1a1a1a" }}>师资介绍</h1>
          <span className="text-xs" style={{ color: "#aaa" }}>严选持证 · 服务有保障</span>
        </div>
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {filterTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveFilter(tab)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
              style={{
                background: activeFilter === tab ? "#D4434F" : "#F5F5F5",
                color: activeFilter === tab ? "#fff" : "#888",
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-4 space-y-3">
        {error ? (
          <div
            className="rounded-2xl px-4 py-3 text-sm"
            style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
          >
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="py-10 text-center text-sm" style={{ color: "#AAA" }}>加载中...</div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm" style={{ color: "#AAA" }}>暂无技师数据</div>
        ) : filtered.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-2xl overflow-hidden cursor-pointer"
            style={{ border: "1px solid #F2F2F2" }}
            onClick={() => setSelected(selected === item.id ? null : item.id)}
          >
            <div className="p-4 flex gap-3">
              <div className="relative flex-shrink-0">
                <div className="w-18 h-18 rounded-2xl overflow-hidden" style={{ width: 72, height: 72 }}>
                  <img src={item.avatar} alt={item.name} className="w-full h-full object-cover object-top" />
                </div>
                {item.badge ? (
                  <span
                    className="absolute -top-1.5 -right-1 text-white rounded-full font-semibold whitespace-nowrap"
                    style={{ background: "#D4434F", fontSize: 9, padding: "2px 6px" }}
                  >
                    {item.badge}
                  </span>
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold" style={{ color: "#1a1a1a" }}>{item.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#F5F5F5", color: "#888" }}>{item.title}</span>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i
                      className={selected === item.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
                      style={{ color: "#ccc" }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-sm" />
                    </div>
                    <span className="text-sm font-bold" style={{ color: "#1a1a1a" }}>{item.rating}</span>
                  </div>
                  <span className="text-xs" style={{ color: "#bbb" }}>|</span>
                  <span className="text-xs" style={{ color: "#888" }}>{item.experience}</span>
                  <span className="text-xs" style={{ color: "#bbb" }}>|</span>
                  <span className="text-xs" style={{ color: "#888" }}>{item.orders}单</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: "#FFF0F1", color: "#D4434F" }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {selected === item.id ? (
              <div className="px-4 pb-4" style={{ borderTop: "1px solid #F5F5F5" }}>
                <p className="text-xs leading-relaxed pt-3 mb-3" style={{ color: "#888" }}>{item.intro}</p>
                <div className="mb-3">
                  <p className="text-xs font-semibold mb-1.5" style={{ color: "#666" }}>擅长项目</p>
                  <div className="flex flex-wrap gap-1.5">
                    {item.specialty.map((specialty) => (
                      <span
                        key={specialty}
                        className="text-xs px-2.5 py-1 rounded-full"
                        style={{ background: "#F5F5F5", color: "#555" }}
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/therapist-detail?id=${item.id}&showService=true`);
                  }}
                  className="w-full py-2.5 rounded-full text-white text-sm font-bold cursor-pointer whitespace-nowrap"
                  style={{ background: "#D4434F" }}
                >
                  预约 {item.name} 技师
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <BottomNav />
    </div>
  );
}
